// Typisierte fetch()-Wrapper gegen den Hono API-Server
// Basis-URL kommt aus Vite-Proxy (dev) oder relativen Pfaden (prod via nginx)

const BASE = "/api";

// "Ansicht als" (Admin-Impersonation): Ziel wird lokal gehalten und als
// Header X-View-As an jeden Request gehängt. Backend prüft Admin-Recht.
const VIEW_AS_KEY = "view_as";
// "Ansicht als" ist eine flüchtige Vorschau: sessionStorage statt localStorage,
// damit sie beim Schließen des Browsers automatisch endet (Marie, 2026-07-08 —
// die Vorschau hatte sonst die Nacht überlebt und verwirrte am Folgetag).
export function getViewAs(): { id: number; name: string } | null {
  const raw = sessionStorage.getItem(VIEW_AS_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as { id: number; name: string };
  } catch {
    return null;
  }
}
export function setViewAs(id: number, name: string): void {
  sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify({ id, name }));
}
export function clearViewAs(): void {
  sessionStorage.removeItem(VIEW_AS_KEY);
  // Altbestand aus früherer localStorage-Variante mit aufräumen
  localStorage.removeItem(VIEW_AS_KEY);
}

/**
 * Fehler einer Anfrage: ein gewöhnlicher `Error` mit der Meldung des Servers. Zusätzlich trägt
 * er den Statuscode der Antwort (404 = nicht gefunden, 500 = Serverfehler); wo es gar keine
 * Antwort gab (Netzfehler), fehlt er.
 */
export type ApiFehler = Error & { status?: number };

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  // Dev-Token (localStorage) ODER Session-Cookie (credentials: include)
  // Backend prüft Cookie zuerst, fällt dann auf Bearer zurück.
  const token = localStorage.getItem("auth_token") ?? "";
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options?.headers as Record<string, string> | undefined) ?? {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const va = getViewAs();
  if (va) headers["X-View-As"] = String(va.id);

  const response = await fetch(`${BASE}${path}`, {
    ...options,
    credentials: "include",
    headers,
  });

  if (response.status === 401) {
    // Abgelaufene Session / ungültiger Token → Login
    if (!path.startsWith("/auth/")) {
      localStorage.removeItem("auth_token");
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    const fehler: ApiFehler = new Error(
      (error as { message?: string }).message ?? "Request failed",
    );
    fehler.status = response.status;
    throw fehler;
  }

  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// --- Auth API ---
export type Role = "admin" | "hrbp" | "manager" | "viewer" | "kpi_viewer" | "vorstand";
export type AuthProvider = "microsoft" | "local" | "dev";

export interface MeResponse {
  userId: string;
  role: Role;
  email?: string;
  displayName?: string;
  provider?: AuthProvider;
  personioEmployeeId?: number;
}

export interface AuthConfig {
  /** "hub" = Betrieb hinter dem Agent Hub (SSO läuft dort), "standalone" = eigenes Login. */
  mode?: "hub" | "standalone";
  microsoftEnabled: boolean;
  localLoginEnabled: boolean;
}

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  provider: AuthProvider;
  personioEmployeeId: number | null;
  assignedEmployeeIds: number[];
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
  disabled: boolean;
}

export interface ViewAsCandidate {
  personioEmployeeId: number;
  name: string;
  reportCount: number;
}

export const authApi = {
  config: () => api.get<AuthConfig>("/auth/config"),
  me: () => api.get<MeResponse>("/auth/me"),
  loginLocal: (username: string, password: string) =>
    api.post<{ ok: boolean; user: MeResponse }>("/auth/local", { username, password }),
  logout: () => api.post<{ ok: boolean }>("/auth/logout", {}),
  viewAsCandidates: () => api.get<{ candidates: ViewAsCandidate[] }>("/auth/view-as-candidates"),
};

// nutzt api.get → sendet X-View-As mit, damit die Sidebar im "Ansicht als"-Modus
// das Menü des Managers (nicht des Admins) zeigt
export async function getAllowedBoards(): Promise<string[]> {
  const data = await api.get<{ boards: string[] }>("/auth/boards");
  return data.boards;
}

export const adminUsersApi = {
  list: () => api.get<{ users: AdminUser[] }>("/admin/users"),
  update: (
    id: string,
    patch: Partial<
      Pick<
        AdminUser,
        "role" | "displayName" | "assignedEmployeeIds" | "personioEmployeeId" | "disabled"
      >
    >,
  ) => api.patch<{ user: AdminUser }>(`/admin/users/${id}`, patch),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/admin/users/${id}`),
};

// --- AI API Types ---

export interface PatternInsight {
  id: string;
  severity: "critical" | "warning" | "info";
  title: string;
  description: string;
  affectedCount: number;
  cohort: string;
  recommendation: string;
  confidence: number;
}

export interface PatternsResponse {
  insights: PatternInsight[];
  summary: string;
  trendDelta: number;
  earlyWarnings: string[];
}

export interface BriefingResponse {
  managerId: string;
  briefing: string;
}

// --- AI API Calls ---

// --- Exit Pattern Analysis Types ---

export interface SignalLift {
  signalKey: string;
  signalLabel: string;
  leaverFrequency: number;
  activeFrequency: number;
  lift: number;
  leaverCount: number;
  activeCount: number;
  baselineWeight: number;
  calibratedWeight: number;
}

export interface ExitInsight {
  id: string;
  severity: "critical" | "warning" | "info";
  title: string;
  description: string;
  affectedSegment: string;
  recommendation: string;
  confidence: number;
}

export interface ExitPatternAnalysis {
  totalExits: number;
  avgTenureAtExitMonths: number;
  tenureDistribution: Array<{ bucket: string; count: number; pct: number }>;
  exitByDepartment: Array<{
    department: string;
    exits: number;
    activeCount: number;
    exitRate: number;
  }>;
  exitByCategory: Array<{
    category: string;
    label: string;
    exits: number;
    activeCount: number;
    exitRate: number;
  }>;
  signalLifts: SignalLift[];
  topExitReasons: Array<{ reason: string; count: number }>;
  calibratedWeights: Record<string, number>;
  insights: {
    insights: ExitInsight[];
    weightAdjustments: Array<{
      signalKey: string;
      currentWeight: number;
      suggestedWeight: number;
      rationale: string;
    }>;
    summary: string;
    dataQuality: "sufficient" | "limited" | "insufficient";
  };
}

export const aiApi = {
  getPatterns: () => api.get<PatternsResponse>("/ai/patterns"),
  getBriefing: (managerId: string) => api.post<BriefingResponse>(`/ai/briefing/${managerId}`, {}),
  getExitPatterns: () => api.get<ExitPatternAnalysis>("/ai/exit-patterns"),
  getEmployeeTips: (id: number) => api.get<EmployeeTipsResponse>(`/ai/employee-tips/${id}`),
};

// --- Employees / Retention API ---

export type RiskLevel = "high" | "medium" | "low";

export interface EmployeeRecord {
  id: number;
  displayName: string;
  department: string | null;
  position: string | null;
  employmentCategory: string;
  team: string | null;
  status: string | null;
  tenureMonths: number | null;
  hireDate: string | null;
  retentionScore: number | null;
  riskLevel: RiskLevel | null;
  confidence: number | null;
  topSignals: string[];
  trendPp?: number | null;
  isConfirmedLeaving?: boolean;
  eventAttendanceRate?: number | null;
  sickDays12m?: number | null;
  sickDays3m?: number | null;
  probationPeriodEnd?: string | null;
  terminationDate?: string | null;
  terminationReason?: string | null;
  personalleiter?: string | null;
  fuehrungskraft?: string | null;
}

export interface EmployeeSummary {
  total: number;
  high: number;
  medium: number;
  low: number;
  avgScore: number;
  confirmedLeaving?: number;
}

export interface EmployeesResponse {
  summary: EmployeeSummary;
  employees: EmployeeRecord[];
  requestId: string;
  historyStatus?: "ok" | "unavailable";
}

export interface BillingSummary {
  ownerId: string;
  ownerName: string;
  ownerEmail: string | null;
  ownerDepartment: string | null;
  totalAmount: number;
  invoiceCount: number;
  avgInvoiceSize: number;
  fiscalYear: number;
  source: "sfcma_invoice" | "berater_projekt" | "opportunity";
}

/** Umsatz einer Abteilung, getrennt nach aktiven und ehemaligen Mitarbeitern. */
export interface DepartmentBilling {
  assigned: number;
  former: number;
}

/**
 * Was die Brücke zwischen Salesforce und Personio NICHT zuordnen konnte.
 * Wird bewusst angezeigt: Stilles Weglassen war die Ursache dafür, dass ein
 * Zuordnungsfehler fünf Wochen unbemerkt blieb.
 */
export interface BillingBridgeSummary {
  unassignedAmount: number;
  unassignedCount: number;
  formerAmount: number;
  formerCount: number;
  /** Externe: fakturieren, sind aber keine Mitarbeiter und stehen nicht in Personio. */
  externalAmount: number;
  externalCount: number;
  excludedAmount: number;
  excludedCount: number;
}

export interface UnassignedOwner {
  ownerId: string;
  ownerName: string;
  ownerEmail: string | null;
  amount: number;
}

export interface BillingResponse {
  year: number;
  totalEmployees: number;
  totalAmount: number;
  employees: BillingSummary[];
  byDepartment: Record<string, DepartmentBilling>;
  summary: BillingBridgeSummary;
  unassignedOwners: UnassignedOwner[];
  requestId: string;
}

export interface TeamRiskEmployee {
  id: number;
  displayName: string;
  score: number;
  riskLevel: RiskLevel;
  topSignals: string[];
  isConfirmedLeaving: boolean;
}

export interface DepartmentRiskProfile {
  department: string;
  total: number;
  high: number;
  medium: number;
  low: number;
  avgScore: number;
  confirmedLeaving: number;
  topRiskEmployees: TeamRiskEmployee[];
  trendPp?: number | null;
}

export interface TeamsResponse {
  summary: EmployeeSummary & { confirmedLeaving: number };
  teams: DepartmentRiskProfile[];
  requestId: string;
}

// --- Employee Detail Analysis ---

export interface RetentionSignal {
  key: string;
  label: string;
  weight: number;
  triggered: boolean;
  positive?: boolean; // stabilisierender Faktor (kein Risiko)
}

export interface EventParticipation {
  quarter: string;
  label: string;
  attended: boolean | null;
  /** true = in Personio explizit gepflegt ("ja"/"nein"); false = aus leerem Feld
   *  abgeleitet. Anzeige (% + ✓/✗) wertet nur gepflegte Quartale (W3). */
  explicit: boolean;
}

export interface EmployeeAnalysis {
  id: number;
  displayName: string;
  pseudoId: string;
  department: string | null;
  position: string | null;
  employmentCategory: string;
  team: string | null;
  status: string | null;
  tenureMonths: number | null;
  hireDate: string | null;
  retentionScore: number;
  riskLevel: RiskLevel;
  confidence: number;
  isConfirmedLeaving: boolean;
  signals: RetentionSignal[];
  calculatedAt: string;
  eventAttendanceRate?: number | null;
  events?: EventParticipation[];
  sickDayStats?: {
    sickDays12m: number;
    sickDays3m: number;
    sickEpisodes12m: number;
    lastSickDate: string | null;
  } | null;
  /** Gehaltsposition: Betrag + Vergleich zur gleichen Rolle/Stufe (nur FK-Sicht).
   *  Vergleich/Median laufen auf VOLLZEIT-Basis (Teilzeit hochgerechnet). */
  salaryPosition?: {
    annualSalary: number;
    fteAnnualSalary: number;
    weeklyHours: number | null;
    group: string;
    groupSize: number;
    median: number | null;
    deltaPct: number | null;
  } | null;
  scoreHistory?: Array<{ date: string; score: number }>;
}

// --- Manager Tips ---

export interface ManagerTip {
  timeframe: "sofort" | "monat" | "quartal";
  action: string;
  coreValue: string;
  why: string;
}

export interface EmployeeTipsResponse {
  pseudoId: string;
  riskSummary: string;
  tips: ManagerTip[];
  escalateToHrbp: boolean;
  escalateReason?: string;
}

export type EmployeesBoardContext = "onboarding" | "kpi-reporting";

function buildEmployeesQuery(opts?: {
  includeInactive?: boolean;
  boardContext?: EmployeesBoardContext;
}): string {
  const params = new URLSearchParams();
  if (opts?.includeInactive) params.set("includeInactive", "true");
  if (opts?.boardContext) params.set("boardContext", opts.boardContext);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const employeesApi = {
  getAll: () => api.get<EmployeesResponse>("/employees"),
  getAllWithInactive: () => api.get<EmployeesResponse>("/employees?includeInactive=true"),
  /**
   * Like getAll/getAllWithInactive but allows specifying a board context
   * (activates 12-month history window for onboarding / kpi-reporting).
   */
  getAllForBoard: (opts: { includeInactive?: boolean; boardContext: EmployeesBoardContext }) =>
    api.get<EmployeesResponse>(`/employees${buildEmployeesQuery(opts)}`),
  getSummary: () => api.get<EmployeeSummary>("/employees/summary"),
  getTeams: () => api.get<TeamsResponse>("/employees/teams"),
  getBilling: (year?: number) =>
    api.get<BillingResponse>(`/employees/billing${year ? `?year=${year}` : ""}`),
  getAnalysis: (id: number) => api.get<EmployeeAnalysis>(`/employees/${id}/analysis`),
  getSurvivalCurves: (
    groupBy?: "department" | "category" | "cohort_year",
    deptPerspective?: "entry" | "exit",
  ) => {
    const params = new URLSearchParams();
    if (groupBy) params.set("groupBy", groupBy);
    if (deptPerspective) params.set("deptPerspective", deptPerspective);
    const qs = params.toString();
    return api.get<SurvivalCurvesResponse>(`/employees/survival-curves${qs ? `?${qs}` : ""}`);
  },
  getRiskTrend: () =>
    api.get<{
      available: boolean;
      since?: string;
      points: Array<{ date: string; avgScore: number; highRiskCount: number }>;
    }>("/employees/risk-trend"),
  getAttritionTrend: () =>
    api.get<{ quarters: Array<{ quarter: string; hires: number; exits: number; net: number }> }>(
      "/employees/attrition-trend",
    ),
  getTurnoverCosts: (year?: number) =>
    api.get<TurnoverCostSummary>(`/employees/turnover-costs${year ? `?year=${year}` : ""}`),
  getManagerEffectiveness: () =>
    api.get<{ effectiveness: ManagerEffectiveness[] }>("/employees/manager-effectiveness"),
  getLrValidation: () => api.get<LRValidationResult>("/employees/lr-validation"),
  getTrainingReadiness: () => api.get<TrainingReadiness>("/employees/training-readiness"),
  getMonthlyBilling: (months?: number) =>
    api.get<MonthlyBillingResponse>(
      `/employees/billing/monthly${months ? `?months=${months}` : ""}`,
    ),
};

// --- Monthly Billing per Consultant ---

export interface MonthlyBillingEntry {
  yearMonth: string; // "2025-03"
  amount: number;
  projectCount: number;
}

export interface ConsultantMonthlyBilling {
  consultantId: string;
  personioId: number | null;
  consultantName: string;
  consultantEmail: string | null;
  department: string | null;
  months: MonthlyBillingEntry[]; // neueste zuerst, Länge = months-back
  total12m: number;
  total3m: number;
  avgMonthly: number;
  trend: "up" | "flat" | "down";
}

export interface MonthlyBillingResponse {
  consultants: ConsultantMonthlyBilling[];
  months: string[]; // alle YYYY-MM im Zeitraum, neueste zuerst
  totalByMonth: Record<string, number>;
  requestId: string;
}

// --- Survival Curves ---

export interface SurvivalPoint {
  monthsSinceHire: number;
  survivorPct: number;
  atRisk: number;
}

export interface SurvivalCurve {
  groupLabel: string;
  color: string;
  points: SurvivalPoint[];
  medianSurvivalMonths: number | null;
}

export interface SurvivalCurvesResponse {
  curves: SurvivalCurve[];
  groupBy: string;
}

// --- Turnover Costs ---

export interface TurnoverCostItem {
  employeeId: number;
  displayName: string;
  department: string | null;
  annualBilling: number;
  attritionProbability: number;
  replacementCost: number;
  rampLossCost: number;
  expectedCost: number;
  riskLevel: RiskLevel;
}

export interface TurnoverCostSummary {
  totalExpectedCost: number;
  highRiskCost: number;
  employeeCount: number;
  avgCostPerHighRisk: number;
  items: TurnoverCostItem[];
  billingSummary?: BillingBridgeSummary;
  requestId?: string;
}

// --- Manager Effectiveness ---

export interface ManagerEffectiveness {
  managerId: number | null;
  managerLabel: string;
  department: string | null;
  teamSize: number;
  avgScore: number;
  highRiskCount: number;
  retentionRate: number;
  trend: "improving" | "stable" | "declining";
}

// --- Logistic Regression Validation ---

export interface LRValidationResult {
  coefficients: Record<string, number>;
  accuracy: number;
  precision: number;
  recall: number;
  totalSamples: number;
  positiveSamples: number;
  epochs: number;
  requestId?: string;
}

export interface TrainingReadiness {
  monthsCovered: number;
  firstMonth: string | null;
  lastMonth: string | null;
  snapshotCount: number;
  distinctEmployees: number;
  terminationsTotal: number;
  regrettableYes: number;
  regrettableNo: number;
  regrettableUnassigned: number;
  voluntaryCount: number;
  trainablePositives: number;
  trainableNegatives: number;
  censored: number;
  horizonMonths: number;
}

// --- Interventions ---

export type InterventionType =
  | "einzel_gespraech"
  | "gehaltsreview"
  | "entwicklungsgespraech"
  | "versetzungsangebot"
  | "sonstiges";

export type InterventionStatus = "open" | "in_progress" | "done";

export interface Intervention {
  id: string;
  employeeId: number;
  type: InterventionType;
  status: InterventionStatus;
  notes: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface InterventionStats {
  open: number;
  inProgress: number;
  done: number;
  lastActionAt: string | null;
}

export interface InterventionsResponse {
  interventions: Intervention[];
  stats: InterventionStats;
}

export const interventionsApi = {
  getForEmployee: (employeeId: number) =>
    api.get<InterventionsResponse>(`/interventions/employee/${employeeId}`),
  create: (data: {
    employeeId: number;
    type: InterventionType;
    status?: InterventionStatus;
    notes?: string;
  }) => api.post<{ intervention: Intervention }>("/interventions", data),
  update: (id: string, patch: { status?: InterventionStatus; notes?: string }) =>
    api.patch<{ intervention: Intervention }>(`/interventions/${id}`, patch),
};

// --- KPI Reporting ---

export interface KpiSegment {
  bestand: number;
  zugaenge: number;
  abgaenge: number;
}
export interface KpiMonthHeadcount {
  month: string;
  consulting: KpiSegment;
  nonConsulting: KpiSegment;
  total: KpiSegment;
}

export interface KpiPerson {
  employeeId: number;
  name: string;
  department: string | null;
  bereich: string | null;
  date: string;
}

export interface KpiWechsel {
  employeeId: number;
  name: string;
  date: string;
  von: string;
  nach: string;
}

export interface KpiReport {
  fromMonth: string;
  toMonth: string;
  months: string[];
  headcount: KpiMonthHeadcount[];
  fluctuation: { total: number; consulting: number; nonConsulting: number };
  growth: { total: number; consulting: number; nonConsulting: number };
  fluctuationTrend: Array<{
    month: string;
    total: number;
    consulting: number;
    nonConsulting: number;
  }>;
  terminations: Array<{ month: string; total: number; consulting: number; nonConsulting: number }>;
  /** Leer, wenn die Rolle die Bewertung nicht sehen darf (bedauerlichSichtbar). */
  regrettable: Array<{
    month: string;
    confirmed: number;
    consulting: number;
    nonConsulting: number;
  }>;
  /** false ⇒ „Bedauerlich" bleibt verdeckt: Bewertung eines einzelnen Austritts. */
  bedauerlichSichtbar: boolean;
  terminationDataAvailable: boolean;
  bereich: string | null;
  unterbereich: string | null;
  bereiche: Array<{ code: string; label: string; group: "consulting" | "intern" }>;
  unterbereiche: Record<string, string[]>;
  /** F2: interne Bereichswechsel je Monat — nur bei aktivem Bereichs-Filter gefüllt */
  interneWechsel: Array<{ month: string; zugaenge: number; abgaenge: number }>;
  monthlyNames: Array<{
    month: string;
    eintritte: KpiPerson[];
    abgaenge: KpiPerson[];
    kuendigungen: KpiPerson[];
    bedauerlich: KpiPerson[];
    interneZugaenge: KpiWechsel[];
    interneAbgaenge: KpiWechsel[];
  }>;
}

export interface KpiInsights {
  insights: Array<{
    id: string;
    severity: "critical" | "warning" | "info";
    title: string;
    description: string;
    recommendation: string;
  }>;
  summary: string;
}

/** @deprecated use KpiPerson instead */
export interface KpiLeaver {
  employeeId: number;
  name: string;
  department: string | null;
  bereich: string | null;
  date: string;
}

export interface KpiMonthDetail {
  month: string;
  bereich: string | null;
  eintritte: KpiPerson[];
  austritte: KpiPerson[];
  kuendigungen: KpiPerson[];
  interneZugaenge: KpiWechsel[];
  interneAbgaenge: KpiWechsel[];
}

export interface FkDashboardData {
  from: string;
  to: string;
  teamSize: number;
  teamSizeVorjahr: number;
  highRisk: number;
  avgScore: number | null;
  confirmedLeaving: number;
  abgaengeImZeitraum: number;
  zugaengeImZeitraum: number;
  abgaengeVorperiode: number;
  zugaengeVorperiode: number;
  vergleichVon: string;
  vergleichBis: string;
  series: Array<{
    date: string;
    low: number;
    medium: number;
    high: number;
    avgScore: number | null;
    headcount: number;
  }>;
  namen: {
    hochrisiko: string[];
    zugaenge: string[];
    abgaenge: string[];
    probezeit: string[];
    bestaetigteAbgaenge: string[];
    abgaenge12m: string[];
  };
  einstellungen12m: number;
  fruehAustritte12m: number;
  einstellungenVorjahr: number;
  fruehAustritteVorjahr: number;
  fluktuation12m: number;
  abgaenge12m: number;
  bedauerlich12m: number;
  gespraecheUeberfaellig: number;
  probezeitEnden: number;
  signalRanking: Array<{ key: string; label: string; count: number }>;
}

export const fkDashboardApi = {
  get: (from?: string, to?: string) => api.get<FkDashboardData>(`/fk-dashboard${qp({ from, to })}`),
};

// ── W26: Wirtschaftlichkeit (Mein Team + Drawer-Reiter, Zielbild 03.08.) ──────

export type WirtschaftAmpelLevel = "ok" | "warning" | "critical";

export interface WirtschaftAlert {
  severity: "warning" | "critical";
  message: string;
}

export interface BindungAmpel {
  level: WirtschaftAmpelLevel;
  score: number | null;
  topSignal: string | null;
  isConfirmedLeaving: boolean;
}

export interface MeinTeamRow {
  id: number;
  name: string;
  position: string | null;
  department: string | null;
  team: string | null;
  /**
   * Fuehrungskraft (Consulting-Manager, sonst Vorgesetzter). null, wenn keine
   * hinterlegt ist ODER die Rolle sie nicht sehen darf — Teamleiter sehen nur
   * ihr eigenes Team, dort waere die Spalte ohnehin konstant.
   */
  fuehrungskraft: string | null;
  bindung: BindungAmpel;
  wirtschaft: {
    level: WirtschaftAmpelLevel;
    alerts: WirtschaftAlert[];
    utilizationAvg3m: number | null;
    umsatz12m: number;
    /** DB-Marge 12M — (Umsatz − Kosten) ÷ Umsatz. „Marge“ meint immer diese. */
    deckungsbeitragPct12m: number | null;
    hasCost: boolean;
    /** Zählt in die Ø Auslastung — nur, wenn die Ampel die Auslastung bewertet (16.09.2026). */
    auslastungBewertet: boolean;
    /** Zählt in die Ampelverteilung — nur, wenn die Ampel Regeln hat (16.09.2026). */
    ampelBewertet: boolean;
  };
}

export interface MeinTeamResponse {
  dataBasis: "demo" | "live";
  lastClosedMonth: string;
  rows: MeinTeamRow[];
}

export interface WirtschaftVerlaufPoint {
  yearMonth: string;
  umsatz: number;
  kosten: number | null;
  billedHours: number;
  utilization: number | null;
}

/**
 * Einordnung in die Vergleichsgruppe (Hebel 4). null, wenn sie sich nicht
 * bilden laesst — kein Gehalt hinterlegt oder kein Umsatz im Fenster. Das ist
 * etwas anderes als "durchschnittlich" und muss unterscheidbar bleiben.
 */
export interface WirtschaftKohorte {
  /** Jahresumsatz je Euro Gehalt, OHNE Festpreis. */
  faktor: number;
  /** Festpreis-Umsatz der Person — steht daneben, nicht im Faktor. */
  festpreis12m: number;
  gruppe: string;
  gruppengroesse: number;
  /** null unterhalb der Mindestgruppengroesse (Anonymitaet). */
  p25: number | null;
  median: number | null;
  p75: number | null;
  perzentil: number | null;
}

export interface WirtschaftEmployeeDetail {
  dataBasis: "demo" | "live";
  lastClosedMonth: string;
  id: number;
  position: string | null;
  weeklyWorkingHours: number | null;
  kohorte: WirtschaftKohorte | null;
  kpis: {
    umsatz12m: number;
    kosten12m: number;
    deckungsbeitrag12m: number;
    deckungsbeitragPct12m: number | null;
    kosten3m: number;
    deckungsbeitrag3m: number;
    deckungsbeitragPct3m: number | null;
    utilizationAvg12m: number | null;
    marginPct12m: number | null;
    hourlyRate3m: number | null;
    ekRate3m: number | null;
    marginPct: number | null;
    utilizationAvg3m: number | null;
    /**
     * false in Einheiten ohne Faktura-Ziel (MTB, Entscheidung Timm 12.08.).
     * Die Auslastung wird dort berechnet und angezeigt, aber nicht bewertet.
     */
    utilizationRated: boolean;
    monthlyCost: number | null;
    fixedPriceHours12m: number;
    fixedPriceRevenue12m: number;
    sickDays12m: number;
  };
  ampel: { level: WirtschaftAmpelLevel; alerts: WirtschaftAlert[] };
  verlauf: WirtschaftVerlaufPoint[];
}

export interface WirtschaftGruppe {
  gruppe: string;
  headcount: number;
  umsatz12m: number;
  kosten12m: number;
  /** true, wenn für mindestens eine Rolle kein Kostensatz hinterlegt ist. */
  kostenUnvollstaendig: boolean;
  utilizationAvg3m: number | null;
  /** DB-Marge der Gruppe über die Summen, nicht das Mittel der Einzelwerte. */
  deckungsbeitragPct12m: number | null;
  ampeln: { ok: number; warning: number; critical: number };
}

export interface WirtschaftVerlaufPunkt {
  yearMonth: string;
  umsatz: number;
  kosten: number;
  billedHours: number;
  utilization: number | null;
  headcount: number;
}

/** Steuerungssicht: Gesamt → Teams → Monatsverlauf (ersetzt „Projektumsatz"). */
export interface WirtschaftSteuerung {
  dataBasis: "demo" | "live";
  lastClosedMonth: string;
  gruppe: string | null;
  gesamt: WirtschaftGruppe;
  gruppen: WirtschaftGruppe[];
  verlauf: WirtschaftVerlaufPunkt[];
}

export const wirtschaftApi = {
  getTeam: () => api.get<MeinTeamResponse>("/wirtschaftlichkeit/team"),
  getEmployee: (id: number) =>
    api.get<WirtschaftEmployeeDetail>(`/wirtschaftlichkeit/employee/${id}`),
  getSteuerung: (gruppe?: string) =>
    api.get<WirtschaftSteuerung>(`/wirtschaftlichkeit/steuerung${qp({ gruppe })}`),
};

// ── W19: Kundensegmente im Vergleich (alle Segmente nebeneinander) ───────────

/** Herkunft einer Zeile — steuert Einrückung, Chevron und Sortierung. */
export type KpiZonenArt =
  | "segment"
  | "unterbereich"
  | "ohne-unterbereich"
  | "kleinstzonen"
  | "ohne-zuordnung";

/** Zone als Tupel — die Rück-Zerlegung eines "ITHR · Service" wäre fehleranfällig. */
export interface KpiZonenKey {
  segment: string;
  unterbereich: string | null;
}

export interface KpiZonenMonat {
  month: string;
  bestand: number;
  zugaenge: number;
  abgaenge: number;
}

export interface KpiZonenKennzahlen {
  bestand: number;
  bestandStart: number;
  bestandAvg: number;
  zugaenge: number;
  abgaenge: number;
  /** Bereichswechsel in die / aus der Zone — reine Zählung, nie Namen. */
  interneZugaenge: number;
  interneAbgaenge: number;
  /** null = Rolle ohne Personenbezug: die Bewertung eines Austritts bleibt verdeckt. */
  bedauerlich: number | null;
  /** null = nicht berechenbar (zu kleine Basis) — NIE als 0 anzeigen. */
  fluktuation: number | null;
  wachstum: number | null;
  /** false ⇒ Raten sind null: „—" plus Chip „kleine Basis", Sortierung ans Ende. */
  rateBelastbar: boolean;
  ersterMonatMitBestand: string | null;
}

export interface KpiVergleichsZone {
  key: KpiZonenKey;
  label: string;
  group: "consulting" | "intern" | "ohne";
  art: KpiZonenArt;
  kennzahlen: KpiZonenKennzahlen;
  monate: KpiZonenMonat[];
  /** steuert den Chevron — datengetrieben, nicht geraten. */
  hatUnterbereiche: boolean;
  kinder: KpiVergleichsZone[];
}

export interface KpiSegmentVergleich {
  fromMonth: string;
  toMonth: string;
  months: string[];
  aufgliederung: "keine" | "unterbereich";
  aufgegliedert: string[];
  minBasis: number;
  minZellgroesse: number;
  gesamt: KpiZonenKennzahlen;
  gesamtMonate: KpiZonenMonat[];
  zonen: KpiVergleichsZone[];
  /** false ⇒ ohne Org-Historie gilt die heutige Zuordnung rückwirkend. */
  orgHistorieVerfuegbar: boolean;
  terminationDataAvailable: boolean;
}

export interface KpiSegmentVergleichParams {
  from?: string;
  to?: string;
  aufgliederung?: "keine" | "unterbereich";
  /** leer = alle Segmente aufgliedern; sonst genau diese Codes. */
  segmente?: readonly string[];
}

export const kpiApi = {
  /**
   * Vergleichssicht — EIN Request für alle Segmente. Niemals getReport je
   * Segment in einer Schleife aufrufen: der Server baut je Aufruf einen
   * eigenen Point-in-time-Resolver über alle Mitarbeiter.
   */
  getSegmentVergleich: (params: KpiSegmentVergleichParams = {}) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    const auf = params.aufgliederung === "unterbereich";
    if (auf) qs.set("aufgliederung", "unterbereich");
    // segmente wirkt nur mit Aufgliederung — allein wäre es eine stille Lüge.
    if (auf && params.segmente && params.segmente.length > 0)
      qs.set("segmente", params.segmente.join(","));
    const s = qs.toString();
    return api.get<KpiSegmentVergleich>(`/kpi-reporting/segmente${s ? `?${s}` : ""}`);
  },
  getReport: (from?: string, bereich?: string, to?: string, unterbereich?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    if (bereich) qs.set("bereich", bereich);
    if (bereich && unterbereich) qs.set("unterbereich", unterbereich);
    const s = qs.toString();
    return api.get<KpiReport>(`/kpi-reporting${s ? `?${s}` : ""}`);
  },
  getMonthDetail: (month: string, bereich?: string, unterbereich?: string) =>
    api.get<KpiMonthDetail>(`/kpi-reporting/month/${month}${qp({ bereich, unterbereich })}`),
  getInsights: (bereich?: string, unterbereich?: string) =>
    api.get<KpiInsights>(`/kpi-reporting/insights${qp({ bereich, unterbereich })}`),
  getRecruiting: (from?: string, to?: string, bereich?: string) =>
    api.get<RecruitingFunnel>(`/kpi-reporting/recruiting${qp({ from, to, bereich })}`),
};

export interface RecruitingFunnelStage {
  key: string;
  label: string;
  reached: number;
  conversionFromPrev: number | null;
}
export interface RecruitingSegmentInfo {
  key: string;
  label: string;
  count: number;
}
export interface RecruitingMonthlyPoint {
  month: string;
  reached: Record<string, number>;
}
export interface RecruitingFunnel {
  available: boolean;
  reason?: string;
  syncedAt?: string;
  from?: string;
  to?: string;
  total?: number;
  bereich?: string | null;
  segments?: RecruitingSegmentInfo[];
  stages?: RecruitingFunnelStage[];
  monthly?: RecruitingMonthlyPoint[];
}

// --- Pulsumfrage ---

export type SurveyDimKey =
  | "personalbindung"
  | "weiterempfehlung"
  | "engagement"
  | "belastung"
  | "weiterentwicklung";

export interface SurveyQuestionSummary {
  id: string;
  text: string;
  zustimmung: number | null;
  diffToPrev: number | null; // Differenz zur Vorrunde in Prozentpunkten
  distribution: number[];
  answered: number;
  commentCount: number;
}
export interface SurveyThemeSummary {
  dimension: SurveyDimKey;
  label: string;
  zustimmung: number | null;
  diffToPrev: number | null;
  distribution: number[];
  answered: number;
  commentCount: number;
  questions: SurveyQuestionSummary[];
}
export interface SurveyTrendPoint {
  round: string;
  dims: Record<SurveyDimKey, number | null>;
}
export interface SurveySummary {
  available: boolean;
  rounds: string[];
  round?: string;
  scaleMax?: number;
  teams?: string[];
  teamFilter?: string | null;
  participantCount?: number;
  denominator?: number;
  participationRate?: number | null;
  previousRound?: string | null;
  themes?: SurveyThemeSummary[];
  trend?: SurveyTrendPoint[];
}
export interface SurveyQuestionDetail {
  id: string;
  text: string;
  dimension: SurveyDimKey;
  dimensionLabel: string;
  zustimmung: number | null;
  answered: number;
  answers: Array<{ value: number; label: string; count: number; pct: number }>;
  comments: Array<{ name: string; answerValue: number | null; text: string }>;
}
export interface SurveyHeatmapRow {
  id: string;
  label: string;
  dimension: SurveyDimKey;
  isTheme: boolean;
  overall: number | null;
  cells: Record<string, number | null>;
}
export interface SurveyHeatmap {
  available: boolean;
  segment?: "team" | "employee";
  view?: "zustimmung" | "differenz";
  /** "pct" = Zustimmung % (Team), "scale" = echter Skalenwert 1–5 (Mitarbeiter). */
  valueMode?: "pct" | "scale";
  columns?: Array<{ key: string; label: string }>;
  rows?: SurveyHeatmapRow[];
}
export interface SurveyUploadResult {
  round: string;
  invited: number | null;
  mapped: number;
  unresolvedCount: number;
  unresolved: string[];
  scaleObserved: { min: number; max: number } | null;
  replaced: boolean;
}

function qp(params: Record<string, string | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) u.set(k, v);
  const s = u.toString();
  return s ? `?${s}` : "";
}

export const surveyApi = {
  getSummary: (round?: string, team?: string) =>
    api.get<SurveySummary>(`/surveys/summary${qp({ round, team })}`),
  getQuestion: (qid: string, round?: string, team?: string) =>
    api.get<SurveyQuestionDetail>(
      `/surveys/question/${encodeURIComponent(qid)}${qp({ round, team })}`,
    ),
  getHeatmap: (
    segment: "team" | "employee",
    view: "zustimmung" | "differenz",
    round?: string,
    team?: string,
  ) => api.get<SurveyHeatmap>(`/surveys/heatmap${qp({ segment, view, round, team })}`),
  upload: async (
    file: File,
    opts?: { round?: string; replace?: boolean },
  ): Promise<SurveyUploadResult> => {
    const token = localStorage.getItem("auth_token") ?? "";
    const fd = new FormData();
    fd.append("file", file);
    if (opts?.round) fd.append("round", opts.round);
    if (opts?.replace) fd.append("replace", "true");
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const va = getViewAs();
    if (va) headers["X-View-As"] = String(va.id);
    // KEIN Content-Type setzen — Browser ergänzt multipart-Boundary selbst
    const res = await fetch("/api/surveys/upload", {
      method: "POST",
      credentials: "include",
      headers,
      body: fd,
    });
    const json = (await res.json().catch(() => ({ message: res.statusText }))) as Record<
      string,
      unknown
    >;
    if (!res.ok) throw new Error((json["message"] as string) ?? "Upload fehlgeschlagen");
    return json as unknown as SurveyUploadResult;
  },
};

// ── Probezeit (Spezifikation 2026-09-16, R1–R3, R7) ──────────────────────────
export type ProbezeitAmpel = "keine" | "gruen" | "gelb" | "rot";
export type ProbezeitSatz =
  | "keineWoche"
  | "vollstaendigErreicht"
  | "vollstaendigNichtErreicht"
  | "schonErreicht"
  | "nochKeineBewertung"
  | "laeuft";
export type NichtGerechnetGrund = "mtb" | "stufe" | "segment";

export interface ProbezeitReihenWoche {
  woche: number;
  von: string;
  bis: string;
  zielStunden: number;
  istStunden: number | null;
}

export interface ProbezeitErgebnis {
  stufe: string;
  woche: number;
  wochenGesamt: number;
  restWochen: number;
  leistungenBis: string | null;
  zielStunden: number;
  istStunden: number;
  zielUmsatz: number;
  istUmsatz: number;
  quote: number | null;
  vkZiel: number;
  vkIst: number | null;
  mitFestpreis: boolean;
  vollkosten: number;
  db: number;
  ampel: ProbezeitAmpel;
  satz: ProbezeitSatz;
}

/**
 * Die Wochenreihe schickt nur das Personenfenster. Die Liste streicht sie
 * (`ohneReihe` in `src/routes/probezeit.ts`), deshalb trägt nur dieser Typ sie.
 */
export interface ProbezeitErgebnisMitReihe extends ProbezeitErgebnis {
  reihe: ProbezeitReihenWoche[];
}

export interface ProbezeitStufe {
  vollkostenJeWoche: number;
  vkZiel: number;
  listenpreis: number;
  zielstunden: number[];
}

/** Die Zweige ohne Ergebnis sind in Liste und Personenfenster gleich. */
export type ProbezeitOhneErgebnis =
  | { status: "keineDaten" }
  | { status: "nichtGerechnet"; grund: NichtGerechnetGrund }
  | { status: "kuenftig" };

export type ProbezeitWirtschaft =
  | { status: "gerechnet"; ergebnis: ProbezeitErgebnis }
  | ProbezeitOhneErgebnis;

export type ProbezeitWirtschaftMitReihe =
  | { status: "gerechnet"; ergebnis: ProbezeitErgebnisMitReihe }
  | ProbezeitOhneErgebnis;

export interface ProbezeitZeile {
  personioId: number;
  name: string;
  position: string | null;
  team: string | null;
  wochenstunden: number | null;
  fuehrungskraft: { id: number; name: string | null } | null;
  eintritt: string;
  probezeitEnde: string;
  austrittZum: string | null;
  lage: "laeuft" | "kuenftig";
  imKundensegment: boolean;
  bindung: "low" | "medium" | "high" | null;
  kuendigungBestaetigt: boolean;
  krankheit: { tage: number; meldungen: number; zuletzt: string | null } | null;
  wirtschaft: ProbezeitWirtschaft;
}

export interface ProbezeitListe {
  zeilen: ProbezeitZeile[];
  /** Hängt an den Personen der Liste, nicht an der Rolle (R2). */
  zeigeWirtschaftsspalten: boolean;
  /** Zeitpunkt des letzten nächtlichen Abgleichs, ISO; null, wenn nie gelaufen. */
  stand: string | null;
}

export interface ProbezeitPerson extends Omit<ProbezeitZeile, "wirtschaft"> {
  /** Nur das Personenfenster bekommt die Wochenreihe (R7). */
  wirtschaft: ProbezeitWirtschaftMitReihe;
  stufenwerte: ProbezeitStufe | null;
  stand: string | null;
}

export const probezeitApi = {
  /** HR kann mit `fk` auf eine Führungskraft filtern; Führungskräfte sehen ohnehin nur ihre Linie. */
  getListe: (fk?: number) =>
    api.get<ProbezeitListe>(`/probezeit/liste${qp({ fk: fk?.toString() })}`),
  getPerson: (personioId: number) => api.get<ProbezeitPerson>(`/probezeit/${personioId}`),
};

// --- Notifications ---

export type NotificationRule =
  | "anniversary_12m"
  | "anniversary_24m"
  | "score_worsened"
  | "no_action_30d";

export interface AppNotification {
  id: string;
  employeeId: number;
  rule: NotificationRule;
  message: string;
  seen: boolean;
  triggeredAt: string;
}

export const notificationsApi = {
  getUnseen: () =>
    api.get<{ notifications: AppNotification[]; unseenCount: number }>("/notifications"),
  markSeen: (id: string) => api.post<{ success: boolean }>(`/notifications/${id}/mark-seen`, {}),
};
